/* eslint-disable import/no-unresolved, import/no-extraneous-dependencies */
import Vue from 'vue';
import VueMeta from 'vue-meta';
import VueI18N from 'vue-i18n';
import Vuex from 'vuex';
import VueSearchRouter from './vue-search-router';
import MessageFormat from 'messageformat';
import ldJsonData from './components/ld-json-data';
import textSeo from '../../common/vue/components/search/text/seo.vue';
import transFileDefault from '../../../config/domains/translations/default/messages.nl.yaml';
import transFileIntlIcu from '../../../config/domains/translations/default/messages+intl-icu.nl.yaml';
import * as constants from '../../common/vue/constants';
import defaultValuesFilters from './shared/default-filter-values';
import Assets from './plugins/assets';

const axios = require('axios').default;
const hsRouter = new VueSearchRouter();

export default function () {
    Vue.component('adivare-ld-json-data', ldJsonData);
    Vue.component('adivare-text-seo', textSeo);

    Vue.use(VueMeta);
    Vue.use(VueI18N);
    Vue.use(Vuex);
    Vue.use(Assets, JS_ASSET ?? {});

    // eslint-disable-next-line no-extend-native
    String.prototype.replaceAt = function replaceAt(index, replacement) {
        return this.substr(0, index) + replacement + this.substr(index + replacement.length);
    };

    // This will replace %text% to {text} to fix translation params.
    class CustomFormatter {
        constructor(options = {}) {
            this._locale = options.locale || 'nl';
            this._formatter = new MessageFormat(this._locale);
            this._caches = Object.create(null);
        }

        interpolate(message, values) {
            let fn = this._caches[message];
            if (!fn) {
                while (message.search('(\\%)(\\S+?\\S)(\\%)') !== -1) {
                    // eslint-disable-next-line no-param-reassign
                    message = message.replaceAt(message.search('(\\%)(\\S+?\\S)(\\%)'), '{').replace('%', '}');
                }
                fn = this._formatter.compile(message, this._locale);
                this._caches[message] = fn;
            }

            return [fn(values)];
        }
    }

    let recursiveArrayMerge = function (target, source) {
        let output = Object.assign({}, target);
        if (isObject(target) && isObject(source)) {
            Object.keys(source).forEach(key => {
                if (isObject(source[key])) {
                    if (!(key in target))
                        Object.assign(output, { [key]: source[key] });
                    else
                        output[key] = recursiveArrayMerge(target[key], source[key]);
                } else {
                    Object.assign(output, { [key]: source[key] });
                }
            });
        }

        return output;
    };

    function isObject(item) {
        return (item && typeof item === 'object' && !Array.isArray(item));
    }

    const translationFilePrefixedMessagesNL = {
        messages_nl: recursiveArrayMerge(
            recursiveArrayMerge(
                transFileDefault,
                require('/config/domains/translations/' + process.env.APP_DOMAIN + '/messages.nl.yaml')
            ),
            transFileIntlIcu
        )
    };

    const i18n = new VueI18N({
        locale: 'nl',
        fallbackLocale: 'en',
        formatter: new CustomFormatter(),
        messages: {
            // Object.assign used to make all the translations into one large translation JSON object.
            nl: Object.assign(translationFilePrefixedMessagesNL),
        },
    });

    function makeRequestCreator() {
        var call;
        return function (url, data) {
            if (call) {
                call.cancel();
            }
            call = axios.CancelToken.source();
            return axios.post(url, data, {cancelToken: call.token});
        }
    }

    let get = makeRequestCreator();

    const store = new Vuex.Store({
        state: {
            meta: [
                {}
            ],
            flashes: [],
            ldJsonData: undefined,
            loginHouseCount: undefined,
            houses: [],
            houseCount: undefined,
            houseCountExpected: undefined,
            houseStatuses: true,
            rentalTypesUntranslated: [],
            pending: {
                houses: true,
                counts: true,
            },
            allowUpdates: true,
            userId: undefined,
            userIsPremium: false,
            userFavouriteIds: [],
            seo: {
                text: undefined,
                class: undefined,
            },
            availableFilters: {
                priceFrom: [],
                priceTill: [],
                sortOptions: [],
                radio: {},
                checkbox: {},
            },
            defaultFilters: defaultValuesFilters,
            request: undefined,
            filters: {
                force: false,
                location: {
                    location: undefined,
                    distance: undefined,
                    suggestType: undefined,
                    suggestId: undefined,
                    neighborhoodSlug: undefined,
                    streetSlug: undefined,
                    districtSlug: undefined,
                },
                price: {
                    from: 0,
                    till: 100000,
                },
                properties: {
                    rooms: undefined,
                    livingArea: undefined,
                    deliveryLevel: undefined,
                    rentalType: undefined,
                    outside: [],
                },
                page: 1,
                sorting: undefined,
                resultsPerPage: 25,
                radiusHasChanged: true,
            },
        },
        mutations: {
            updateMeta: (state, payload) => state.meta = payload,
            updateLdJsonData: (state, payload) => state.ldJsonData = payload,
            updateLoginHouseCount: (state, payload) => state.loginHouseCount = payload,
            updateFlashes: (state, payload) => state.flashes = payload,
            updateRentalTypes: (state, payload) => state.rentalTypesUntranslated = payload,
            updateSortSelected: (state, payload) => state.filters.sorting = payload,
            updateDefaultFilters: (state, payload) => state.defaultFilters = payload,
            updateDefaultFiltersLocation: (state, payload) => state.defaultFilters.location = payload,
            toggleFiltersByForce: (state) => state.filters.force = !state.filters.force,
            updateFilters: (state, payload) => state.filters = payload,
            updateRequest: (state, payload) => state.request = payload,
            updateUserId: (state, payload) => state.userId = payload,
            updateUserIsPremium: (state, payload) => state.userIsPremium = payload,
            updateUserFavouriteIds: (state, payload) => state.userFavouriteIds = payload,
            updateHouses: (state, payload) => state.houses = payload,
            updateHouseCount: (state, payload) => state.houseCount = payload,
            updateHouseCountExpected: (state, payload) => state.houseCountExpected = payload,
            updateStatuses: (state, payload) => state.houseStatuses = payload,
            updateAvailableFilters: (state, payload) => state.availableFilters = payload,
            updatePage: (state, payload) => state.filters.page = payload,
            updateCall: (state, payload) => state.call = payload,
            updateSeoText: (state, payload) => state.seo.text = payload,
            updateAllowUpdates: (state, payload) => state.allowUpdates = payload,
            finishWaiting: (state, payload) => state.pending[payload] = false,
            startWaiting: (state, payload) => state.pending[payload] = true,
            toggleFavourite: (state, payload) => {
                if (state.userFavouriteIds.includes(payload.id)) {
                    state.userFavouriteIds = state.userFavouriteIds.filter(value => value !== payload.id);
                } else {
                    state.userFavouriteIds.push(payload.id);
                }
            },
        },
        actions: {
            // update everything from search controller. Trigger this function in child vue templates
            updateFilters: async function (context, payload) {
                context.commit('updateRequest', constants.REQUEST_HOUSES);
                await context.commit('updateFilters', recursiveArrayMerge(context.state.filters, payload));
            },
            // update counts of rentals. Trigger this function in child vue templates
            updateCounts: async function (context, payload) {
                context.commit('updateRequest', constants.REQUEST_COUNTS);
                await context.commit('updateFilters', recursiveArrayMerge(context.state.filters, payload));
            },
            // request everything from search controller
            requestNewHouses: function (context) {
                context.commit('startWaiting', constants.REQUEST_HOUSES);
                context.commit('updateAllowUpdates', false);
                get('/public/api/search', context.state.filters).then((response) => {
                    context.commit('updateHouses', response.data.data.rentals);
                    context.commit('updateAvailableFilters', response.data.data.availableFilters);
                    context.commit('updateSeoText', response.data.data.seo.text);
                    context.commit('updateHouseCount', response.headers['x-total-count']);
                    hsRouter.update(response.data.data.legacyURL).then(() => {
                        context.dispatch(
                            'updateFilters',
                            response.data.data.filters
                        ).then(() => {
                            context.commit('updateAllowUpdates', true);
                            context.commit('updateMeta', response.data.data.meta);
                            context.commit('updateLdJsonData', response.data.data.ldJsonData);
                            context.commit('updateLoginHouseCount', response.data.data.loginHouseCount);
                            context.commit('updateFlashes', response.data.data.flashMessages);
                            context.commit('finishWaiting', constants.REQUEST_HOUSES);
                        })
                    });
                }).catch(function (thrown) {
                    if (false === axios.isCancel(thrown)) {
                        context.commit('finishWaiting', constants.REQUEST_HOUSES);
                        throw(thrown);
                    }
                })
            },
            // only request new counts
            requestNewCounts: function (context) {
                context.commit('startWaiting', constants.REQUEST_COUNTS);
                context.commit('updateAllowUpdates', false);
                get('/public/api/search', context.state.filters, context).then((response) => {
                    context.commit('updateHouseCountExpected', response.headers['x-total-count']);
                    context.dispatch(
                        'updateCounts',
                        response.data.data.filters
                    ).then(() => {
                        context.commit('updateAllowUpdates', true);
                        context.commit('finishWaiting', constants.REQUEST_COUNTS);
                    });
                }).catch(function (thrown) {
                    if (false === axios.isCancel(thrown)) {
                        context.commit('finishWaiting', constants.REQUEST_COUNTS);
                        throw(thrown);
                    }
                })
            },
            // initialize rentals
            initialize: function (context) {
                hsRouter.initialize(context);
                axios.get('/public/api/initialize').then((response) => {
                        context.commit('updateStatuses', response.data.data.houseStatuses);
                        context.commit('updateUserId', response.data.data.userId);
                        context.commit('updateUserIsPremium', response.data.data.userIsPremium);
                        context.commit('updateUserFavouriteIds', response.data.data.userFavouriteIds);
                        context.commit('updateRentalTypes', response.data.data.untranslatedRentalTypes);
                    }
                )

                store.watch((state) => state.request, () => {
                    context.commit('toggleFiltersByForce');
                });

                //Update houses any time the filters change.
                store.watch((state) => state.filters, () => {
                    if (false === context.state.allowUpdates) {
                        return;
                    }
                    switch (context.state.request) {
                        case constants.REQUEST_COUNTS:
                            store.dispatch('requestNewCounts');
                            break;
                        case constants.REQUEST_HOUSES:
                            store.dispatch('requestNewHouses');
                            break;
                        default:
                            return;
                    }
                }, {
                    deep: true
                });
            }
        }
    })

    store.dispatch('initialize');

    return new Vue({
        el: '#vue-searchpage',
        store: store,
        router: hsRouter.router,
        i18n,
        data: {},
        metaInfo() {
            return store.state.meta;
        },
    });
}
